import { useMemo } from "react";
import Workspace from "@/models/workspace/Workspace";
import { Item } from "@/pages/workspace/utils/workspaceItemUtils";
import {
  filterItemsForSearch,
  groupSearchResultsByPath,
  getAllWorkspaceItems,
  sortItemsBy,
} from "@/pages/workspace/utils/workspaceItemUtils";
import { WorkspaceSearch } from "@/pages/workspace/providers/WorkspaceProvider";

interface UseFilesTabSearchProps {
  currentItems: Item[];
  search: WorkspaceSearch;
  workspace: Workspace | null;
  filteredWorkspaceItems: Item[];
  /** Documents the search index found by their text; admitted within each search scope. */
  textMatchIds: ReadonlySet<string>;
  selectedMetadataFilters: { metadataNames: string[] };
  sortBy: string;
  setGroupedSearchResults: (results: { [path: string]: Item[] }) => void;
}

interface UseFilesTabSearchReturn {
  shouldGroupSearchResults: boolean;
  filteredItems: Item[];
  sortedItems: Item[];
}

/**
 * Hook for handling search and filtering logic in FilesTab
 *
 * Manages:
 * - Search result filtering (by search string and metadata)
 * - Search result grouping by folder path
 * - Item sorting (by name, date, etc.)
 *
 * When a search spans multiple folders, results are grouped by their folder path
 * to provide better UX. The hook determines when grouping should occur based on
 * the search criteria.
 */
export const useFilesTabSearch = ({
  currentItems,
  search,
  workspace,
  filteredWorkspaceItems,
  textMatchIds,
  selectedMetadataFilters,
  sortBy,
  setGroupedSearchResults,
}: UseFilesTabSearchProps): UseFilesTabSearchReturn => {
  const shouldGroupSearchResults = useMemo(() => {
    if (workspace) {
      if (search.searchString.trim() !== "") {
        if (search.folders.length === 0) {
          return true;
        }
        if (
          search.folders.length > 1 &&
          !search.folders.some((f) => f.name === "@onlyCurrentFolder")
        ) {
          return true;
        }
        return false;
      }
      return false;
    }
    return false;
  }, [search, workspace]);

  const filteredItems = useMemo(() => {
    let items = currentItems;

    // Search in all folders
    if (shouldGroupSearchResults && search.folders.length === 0) {
      items = filteredWorkspaceItems;
      // Group the results by path for better organization
      const grouped = groupSearchResultsByPath(items, sortBy);
      setGroupedSearchResults(grouped);
    } else {
      // Clear grouped results when not searching the whole workspace
      setGroupedSearchResults({});
    }

    // Search in current folder
    if (
      search.searchString.trim() !== "" &&
      search.folders.some((f) => f.name === "@onlyCurrentFolder") &&
      workspace
    ) {
      items = filterItemsForSearch(currentItems, search, textMatchIds);
    }

    // Filter by folder contents - only show documents within selected folders
    if (
      search.folders.length > 0 &&
      !search.folders.some((f) => f.name === "@onlyCurrentFolder") &&
      search.searchString.trim() !== "" &&
      workspace
    ) {
      // Get the flattened list of all workspace items
      const allItems = getAllWorkspaceItems(workspace);

      // Collect the selected folder names
      const selectedFolderNames = new Set(
        search.folders.map((f) => f.name.toLowerCase())
      );

      // Filter to items that are inside one of the selected folders
      const itemsInSelectedFolders = allItems.filter((item) => {
        // Handle items with no path (root items)
        if (!item.filePath && selectedFolderNames.has("root")) return true;
        if (!item.filePath) return false;

        const pathParts = item.filePath.split("/").filter(Boolean);

        // Get the parent folder (the folder this item is *inside of*)
        const parentFolderName =
          pathParts.length > 1
            ? pathParts[pathParts.length - 2].toLowerCase()
            : "root";

        // --- For folders ---
        // If this is a folder, include it only if its parent folder matches
        if (item.type === "folder") {
          return Array.from(selectedFolderNames).some(
            (folderName) => folderName === parentFolderName
          );
        }

        // --- For documents and links ---
        // Same rule — include only if the item's parent folder matches
        if (item.type === "document" || item.type === "link") {
          return Array.from(selectedFolderNames).some(
            (folderName) => folderName === parentFolderName
          );
        }

        return false;
      });

      // Apply the search string (include metadata when searchOnMetadata is true)
      const filteredItems = filterItemsForSearch(
        itemsInSelectedFolders,
        search,
        textMatchIds
      );

      // Group the results by path for display
      if (search.folders.length !== 1) {
        const grouped = groupSearchResultsByPath(filteredItems, sortBy);
        setGroupedSearchResults(grouped);
      } else {
        setGroupedSearchResults({});
      }
      // Finally, set filtered items for rendering
      items = filteredItems;
    }

    // Apply metadata filters to current items (always active)
    if (selectedMetadataFilters.metadataNames.length > 0) {
      items = items.filter((item) => {
        if (item.type === "document" && item.documentMetadataList) {
          return item.documentMetadataList.some((metadata) =>
            selectedMetadataFilters.metadataNames.includes(metadata.name)
          );
        }
        if (item.type === "folder") {
          return item;
        }
        return false;
      });
    }

    return items;
  }, [
    currentItems,
    search,
    workspace,
    filteredWorkspaceItems,
    textMatchIds,
    selectedMetadataFilters,
    sortBy,
    shouldGroupSearchResults,
    setGroupedSearchResults,
  ]);

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      // Always show folders first
      return sortItemsBy(a, b, sortBy);
    });
  }, [filteredItems, sortBy]);

  return {
    shouldGroupSearchResults,
    filteredItems,
    sortedItems,
  };
};
